import { Outlet, Link, createRootRoute, HeadContent, Scripts, ScriptOnce } from "@tanstack/react-router";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SiteFeedback } from "@/components/SiteFeedback";
import { Toaster } from "@/components/ui/sonner";
import { SITE_URL, SITE_NAME } from "@/lib/seo";
import { AuthProvider } from "@/lib/auth-context";
import appCss from "../styles.css?url";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold" style={{ fontFamily: "'Syne', sans-serif" }}>404</h1>
        <h2 className="mt-4 text-xl font-semibold">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => {
    const umamiId = (import.meta.env.VITE_UMAMI_WEBSITE_ID as string | undefined) || "83b61bf3-d750-4bfc-a909-1a014d9a9d4a";
    const umamiSrc = (import.meta.env.VITE_UMAMI_SRC as string | undefined) || "https://cloud.umami.is/script.js";
    const orgJsonLd = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
      logo: `${SITE_URL}/logo.png`,
      sameAs: [],
    };
    const websiteJsonLd = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: SITE_NAME,
      url: SITE_URL,
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE_URL}/discover?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    };
    return {
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        { name: "author", content: "1uplift LLC" },
        { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
        { name: "geo.region", content: "US-FL" },
        { name: "geo.placename", content: "Lakewood Ranch" },
        { name: "geo.position", content: "27.4015;-82.3959" },
        { name: "ICBM", content: "27.4015, -82.3959" },
        { name: "theme-color", content: "#FF5E00" },
        { name: "google-site-verification", content: "T3qq5z1lLoMXubSt9voQCwxvlDRYUgmYk95iaDAoj2w" },
        { property: "og:type", content: "website" },
        { property: "og:site_name", content: SITE_NAME },
        { property: "og:locale", content: "en_US" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [
        { rel: "stylesheet", href: appCss },
        { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
        { rel: "shortcut icon", type: "image/svg+xml", href: "/favicon.svg" },
        { rel: "apple-touch-icon", href: "/favicon.svg" },
        { rel: "preconnect", href: "https://fonts.googleapis.com" },
        { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
        { rel: "dns-prefetch", href: "https://wvizaoiiioakdhlqfbov.supabase.co" },
        { rel: "preload", as: "font", type: "font/woff2", crossOrigin: "anonymous", href: "https://fonts.gstatic.com/s/syne/v24/8vIH7w4qzmVxm2BL9G78HEY.woff2" },
        { rel: "preload", as: "font", type: "font/woff2", crossOrigin: "anonymous", href: "https://fonts.gstatic.com/s/spacegrotesk/v22/V8mDoQDjQSkFtoMM3T6r8E7mPbF4C_k3HqU.woff2" },
        { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Caveat:wght@500;700&family=Syne:wght@600;700;800&family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500;600;700;800;900&display=swap" },
      ],
      scripts: umamiId
        ? [
            { src: umamiSrc, "data-website-id": umamiId, defer: true } as Record<string, string | boolean>,
            { type: "application/ld+json", children: JSON.stringify(orgJsonLd) },
            { type: "application/ld+json", children: JSON.stringify(websiteJsonLd) },
          ]
        : [
            { type: "application/ld+json", children: JSON.stringify(orgJsonLd) },
            { type: "application/ld+json", children: JSON.stringify(websiteJsonLd) },
          ],
    };
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }} suppressHydrationWarning>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  return (
    <AuthProvider>
    <div className="flex flex-col min-h-screen">
      <ScriptOnce>{`(function(){try{var s=localStorage.getItem("rp-theme");var d=s==="dark"||(!s&&window.matchMedia&&window.matchMedia("(prefers-color-scheme: dark)").matches);if(d)document.documentElement.classList.add("dark");}catch(e){}})();`}</ScriptOnce>
      <a href="#main-content" className="skip-to-content">
        Skip to main content
      </a>
      <Header />
      <main id="main-content" tabIndex={-1} className="flex-1 pt-[112px] sm:pt-14 focus:outline-none">
        <Outlet />
      </main>
      <Footer />
      <SiteFeedback />
      <Toaster position="top-right" />
    </div>
    </AuthProvider>
  );
}
